// CRF Validation utility based on stored procedure analysis
export const CRF_VALIDATION_CODES = {
  SUCCESS: 0,
  DUPLICATE_RECORD_FOUND: 1,
  CONFIG_DUPLICATE_RECORD_FOUND: 2,
  MODIFIED_BY_OTHER_USER: 3,
  PROJECT_ALREADY_TERMINATED: 4,
  PROJECT_ALREADY_CLOSED: 5,
  PROJECT_ALREADY_INVOICED: 6,
  PROJECT_PAYMENT_RECEIVED: 7,
  INVALID_LOCATION: 8,
  INVALID_MODEL_DATA: 9,
  MISSING_REQUIRED_FIELDS: 10
};

export const CRF_STATUS = {
  ACTIVE: 'ACT',
  TERMINATED: 'TER',
  CLOSED: 'CLO',
  INVOICED: 'INV',
  INACTIVE: 'INA'
};

export const CRF_LOCATIONS = {
  CARSON: 'CARSON',
  CHICAGO: 'CHICAGO',
  BOTH: 'BOTH'
};

export const validateCrfHeader = (formData) => {
  const errors = [];
  
  // Required field validations
  if (!formData.requestorName?.trim()) {
    errors.push({ field: 'requestorName', message: 'Requestor Name is required' });
  }
  
  if (!formData.businessGroup?.trim()) {
    errors.push({ field: 'businessGroup', message: 'Business Group is required' });
  }
  
  if (!formData.budgetCode?.trim()) {
    errors.push({ field: 'budgetCode', message: 'Budget Code is required' });
  }
  
  if (!formData.picContact?.trim()) {
    errors.push({ field: 'picContact', message: 'PIC Contact is required' });
  }
  
  if (!formData.emailAddress?.trim()) {
    errors.push({ field: 'emailAddress', message: 'Email Address is required' });
  }
  
  if (!formData.typeOfWork?.trim()) {
    errors.push({ field: 'typeOfWork', message: 'Type of Work is required' });
  }
  
  if (!formData.workToBePerformed?.trim()) {
    errors.push({ field: 'workToBePerformed', message: 'Work to be Performed is required' });
  }
  
  if (!formData.problemDescription?.trim()) {
    errors.push({ field: 'problemDescription', message: 'Problem Description is required' });
  }
  
  // Email validation
  if (formData.emailAddress?.trim()) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = formData.emailAddress.split(';').map(e => e.trim());
    const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      errors.push({ 
        field: 'emailAddress', 
        message: `Invalid email format: ${invalidEmails.join(', ')}` 
      });
    }
  }
  
  return errors;
};

export const validateDuplicateModels = (models) => {
  const errors = [];
  const validModels = models.filter(model => model.model?.trim());
  
  if (validModels.length <= 1) return errors;
  
  // Check for duplicate models using composite key (From + To)
  // Same 'From' model is allowed if 'To' model is different
  // Same 'To' model is allowed if 'From' model is different
  const modelKeys = {};
  validModels.forEach((model, index) => {
    const fromCode = model.model.trim().toUpperCase();
    const toValue = model.to?.trim()?.toUpperCase() || '';
    const cleanTo = (toValue && toValue !== '0' && toValue !== '"0"' && toValue !== '&QUOT;0&QUOT;') ? toValue : '';
    const compositeKey = `${fromCode}|${cleanTo}`;
    if (modelKeys[compositeKey]) {
      errors.push({
        field: `models[${index}].model`,
        message: `Duplicate model found: "${model.model}"${cleanTo ? ` to "${model.to}"` : ''} appears in multiple rows`
      });
    } else {
      modelKeys[compositeKey] = { index, originalValue: model.model };
    }
  });
  
  return errors;
};

export const validateCrfModels = (models, formData) => {
  const errors = [];
  
  if (!models || models.length === 0) {
    errors.push({ field: 'models', message: 'At least one model is required' });
    return errors;
  }
  
  // Check for at least one valid model
  const validModels = models.filter(model => model.model?.trim());
  if (validModels.length === 0) {
    errors.push({ field: 'models', message: 'At least one model with valid model code is required' });
    return errors;
  }
  
  // Check for duplicate models
  const duplicateErrors = validateDuplicateModels(models);
  errors.push(...duplicateErrors);
  
  // Validate each model
  models.forEach((model, index) => {
    if (!model.model?.trim()) return; // Skip empty models
    
    const modelPrefix = `Model ${index + 1}`;
    
    // Validate quantities based on location
    if (formData.location === CRF_LOCATIONS.BOTH) {
      if (!model.carsonQuantity || parseInt(model.carsonQuantity) <= 0) {
        errors.push({ 
          field: `models[${index}].carsonQuantity`, 
          message: `${modelPrefix}: Carson quantity is required and must be greater than 0` 
        });
      }
      if (!model.chicagoQuantity || parseInt(model.chicagoQuantity) <= 0) {
        errors.push({ 
          field: `models[${index}].chicagoQuantity`, 
          message: `${modelPrefix}: Chicago quantity is required and must be greater than 0` 
        });
      }
    } else {
      if (!model.quantity || parseInt(model.quantity) <= 0) {
        errors.push({ 
          field: `models[${index}].quantity`, 
          message: `${modelPrefix}: Quantity is required and must be greater than 0` 
        });
      }
    }
    
    // Validate date required
    if (!model.dateRequired) {
      errors.push({ 
        field: `models[${index}].dateRequired`, 
        message: `${modelPrefix}: Date required is mandatory` 
      });
    }
    
    // Validate 'To' field for CFG and DFG work types
    // Only check if 'to' field has a meaningful value (not empty, not "0")
    if (formData.typeOfWork === 'CFG' || formData.typeOfWork === 'DFG') {
      const toValue = model.to?.trim();
      if (!toValue || toValue === '0' || toValue === '"0"' || toValue === '&quot;0&quot;') {
        errors.push({ 
          field: `models[${index}].to`, 
          message: `${modelPrefix}: 'To' field is required for ${formData.typeOfWork} work type` 
        });
      }
    }
  });
  
  return errors;
};

export const validateCrfForSubmission = (formData, models) => {
  const headerErrors = validateCrfHeader(formData);
  const modelErrors = validateCrfModels(models, formData);
  
  return [...headerErrors, ...modelErrors];
};

export const formatValidationErrors = (errors) => {
  if (!errors || errors.length === 0) return '';
  
  return errors.map(error => error.message).join(', ');
};

export const getFirstErrorField = (errors) => {
  if (!errors || errors.length === 0) return null;
  
  const fieldMap = {
    'requestorName': 'requestorNameRef',
    'businessGroup': 'businessGroupRef',
    'budgetCode': 'budgetCodeRef',
    'picContact': 'picContactRef',
    'emailAddress': 'emailAddressRef',
    'typeOfWork': 'typeOfWorkRef',
    'workToBePerformed': 'workToBePerformedRef',
    'problemDescription': 'problemDescriptionRef'
  };
  
  const firstError = errors[0];
  return fieldMap[firstError.field] || null;
};

// Business logic validation based on stored procedure
export const validateBusinessRules = (formData, models) => {
  const errors = [];
  
  // Check if location is BOTH and separate invoice is required
  if (formData.location === CRF_LOCATIONS.BOTH) {
    // Validate that both Carson and Chicago quantities are provided
    const bothLocationModels = models.filter(m => m.model?.trim());
    const invalidBothModels = bothLocationModels.filter(m => 
      !m.carsonQuantity || !m.chicagoQuantity || 
      parseInt(m.carsonQuantity) <= 0 || parseInt(m.chicagoQuantity) <= 0
    );
    
    if (invalidBothModels.length > 0) {
      errors.push({
        field: 'models',
        message: 'When location is BOTH, all models must have valid Carson and Chicago quantities'
      });
    }
  }
  
  // Validate department requirement for BBSC business group
  if (formData.businessGroup === 'BBSC' && !formData.department?.trim()) {
    errors.push({
      field: 'department',
      message: 'Department is required for BBSC business group'
    });
  }
  
  return errors;
};

export const CrfValidationUtils = {
  validateCrfHeader,
  validateCrfModels,
  validateDuplicateModels,
  validateCrfForSubmission,
  validateBusinessRules,
  formatValidationErrors,
  getFirstErrorField,
  CRF_VALIDATION_CODES,
  CRF_STATUS,
  CRF_LOCATIONS
};